import { useRef, useEffect, useState, useCallback, type PointerEvent as ReactPointerEvent } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import BrushIcon from '@mui/icons-material/Brush';
import UndoIcon from '@mui/icons-material/Undo';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';

type Tool = 'arrow' | 'rect' | 'redact' | 'pen';

interface Shape {
  tool: Tool;
  color: string;
  points: number[]; // arrow/rect/redact: [x1,y1,x2,y2]; pen: [x1,y1,x2,y2,...]
}

const COLORS = ['#e53935', '#fb8c00', '#fdd835', '#43a047', '#1e88e5', '#000000'];
const MAX_W = 1920;
const LINE = 3;

interface Props {
  src: string;
  onChange: (dataUrl: string) => void;
}

export function ScreenshotEditor({ src, onChange }: Props) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const shapesRef = useRef<Shape[]>([]);
  const draftRef = useRef<Shape | null>(null);
  const [tool, setTool] = useState<Tool>('redact');
  const [color, setColor] = useState<string>(COLORS[0]!);
  const [ready, setReady] = useState(false);

  const drawShape = (ctx: CanvasRenderingContext2D, s: Shape) => {
    const p = s.points;
    ctx.lineWidth = LINE;
    ctx.strokeStyle = s.color;
    ctx.fillStyle = s.color;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (s.tool === 'redact') {
      ctx.fillStyle = '#000000';
      const x = Math.min(p[0]!, p[2]!), y = Math.min(p[1]!, p[3]!);
      ctx.fillRect(x, y, Math.abs(p[2]! - p[0]!), Math.abs(p[3]! - p[1]!));
    } else if (s.tool === 'rect') {
      const x = Math.min(p[0]!, p[2]!), y = Math.min(p[1]!, p[3]!);
      ctx.strokeRect(x, y, Math.abs(p[2]! - p[0]!), Math.abs(p[3]! - p[1]!));
    } else if (s.tool === 'pen') {
      ctx.beginPath();
      ctx.moveTo(p[0]!, p[1]!);
      for (let i = 2; i < p.length; i += 2) ctx.lineTo(p[i]!, p[i + 1]!);
      ctx.stroke();
    } else if (s.tool === 'arrow') {
      const [x1, y1, x2, y2] = p as [number, number, number, number];
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      const ang = Math.atan2(y2 - y1, x2 - x1);
      const head = 12;
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - head * Math.cos(ang - Math.PI / 6), y2 - head * Math.sin(ang - Math.PI / 6));
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - head * Math.cos(ang + Math.PI / 6), y2 - head * Math.sin(ang + Math.PI / 6));
      ctx.stroke();
    }
  };

  const redraw = useCallback((includeDraft = true) => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const s of shapesRef.current) drawShape(ctx, s);
    if (includeDraft && draftRef.current) drawShape(ctx, draftRef.current);
  }, []);

  const emit = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) onChange(canvas.toDataURL('image/png'));
  }, [onChange]);

  // Bild laden + Canvas dimensionieren.
  useEffect(() => {
    setReady(false);
    shapesRef.current = [];
    draftRef.current = null;
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const scale = Math.min(1, MAX_W / (img.naturalWidth || MAX_W));
      canvas.width = Math.round((img.naturalWidth || MAX_W) * scale);
      canvas.height = Math.round((img.naturalHeight || MAX_W) * scale);
      setReady(true);
      redraw();
    };
    img.src = src;
  }, [src, redraw]);

  const toCanvasXY = (e: ReactPointerEvent<HTMLCanvasElement>): [number, number] => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    // clientWidth/Height = Inhaltsbox (ohne Rahmen), clientLeft/Top = Rahmenbreite.
    // So mappt der Klick pixelgenau auf die Bitmap — unabhängig von CSS-Skalierung.
    const sx = canvas.width / canvas.clientWidth;
    const sy = canvas.height / canvas.clientHeight;
    return [(e.clientX - rect.left - canvas.clientLeft) * sx, (e.clientY - rect.top - canvas.clientTop) * sy];
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!ready) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const [x, y] = toCanvasXY(e);
    draftRef.current = { tool, color, points: [x, y, x, y] };
    redraw();
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!draftRef.current) return;
    const [x, y] = toCanvasXY(e);
    const d = draftRef.current;
    if (d.tool === 'pen') d.points.push(x, y);
    else { d.points[2] = x; d.points[3] = y; }
    redraw();
  };

  const onPointerUp = () => {
    const d = draftRef.current;
    if (!d) return;
    draftRef.current = null;
    // Winzige Klicks verwerfen (kein versehentlicher Punkt).
    const isTiny = d.tool !== 'pen' && Math.hypot(d.points[2]! - d.points[0]!, d.points[3]! - d.points[1]!) < 4;
    if (!isTiny) shapesRef.current.push(d);
    redraw(false);
    emit();
  };

  const undo = () => { shapesRef.current.pop(); redraw(false); emit(); };
  const reset = () => { shapesRef.current = []; redraw(false); emit(); };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1, gap: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={tool}
          onChange={(_, v) => v && setTool(v as Tool)}
        >
          <ToggleButton value="redact"><Tooltip title={t('bugReport.toolRedact')}><VisibilityOffIcon fontSize="small" /></Tooltip></ToggleButton>
          <ToggleButton value="arrow"><Tooltip title={t('bugReport.toolArrow')}><NorthEastIcon fontSize="small" /></Tooltip></ToggleButton>
          <ToggleButton value="rect"><Tooltip title={t('bugReport.toolRect')}><CropSquareIcon fontSize="small" /></Tooltip></ToggleButton>
          <ToggleButton value="pen"><Tooltip title={t('bugReport.toolPen')}><BrushIcon fontSize="small" /></Tooltip></ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" spacing={0.5} alignItems="center">
          {COLORS.map((c) => (
            <Box
              key={c}
              onClick={() => setColor(c)}
              sx={{
                width: 20, height: 20, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                border: '2px solid', borderColor: color === c ? 'primary.main' : 'divider',
              }}
            />
          ))}
        </Stack>

        <Tooltip title={t('bugReport.editorUndo')}><span><IconButton size="small" onClick={undo}><UndoIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title={t('bugReport.editorReset')}><span><IconButton size="small" onClick={reset}><RestartAltIcon fontSize="small" /></IconButton></span></Tooltip>
      </Stack>

      <Box sx={{ maxHeight: '72vh', overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Box
          component="canvas"
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          sx={{
            // Keine Border/objectFit am Canvas selbst -> clientWidth/Height == Bitmap-Skalierung, 0 Versatz.
            width: '100%', height: 'auto', display: 'block',
            cursor: 'crosshair', touchAction: 'none',
          }}
        />
      </Box>
      <Box sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary' }}>{t('bugReport.editorHint')}</Box>
    </Box>
  );
}
